import { RenderTexture, Sprite, Texture, type Application } from "pixi.js";
import { ensureReady } from "./texture";
import {
  computePalette,
  dedupePalette,
  sortColors,
  toRgbHexValue,
} from "./palette";

export async function loadImageForPixi(
  file: File
): Promise<ImageBitmap | HTMLImageElement> {
  // 1) Try ImageBitmap (fast path)
  try {
    // no options — Safari hates unknown enums like "from-image"
    return await createImageBitmap(file);
  } catch {
    // 2) Fallback: HTMLImageElement + decode()
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      // if you ever load cross-origin URLs, keep this; for file inputs it’s harmless
      img.crossOrigin = "anonymous";
      img.src = url;
      // modern, Safari-supported
      await img.decode();
      return img;
    } finally {
      // revoke later if you keep the Image around; safe to leave if you return img
      // URL.revokeObjectURL(url);
    }
  }
}

export interface ProcessedImage {
  width: number;
  height: number;
  palette: Array<number>; // rgb hex values
  pixels: Uint32Array; // indices into palette
  src: Texture;
  result: Texture;
}

function detailToThreshold(detail: number): number {
  if (detail <= 1) return 12; // shouldn't do detail 1
  if (detail === 2) return 7;
  if (detail === 3) return 6;
  if (detail === 4) return 5;
  return 1; // shouldn't do detail 5
}

function detailToPixelSize(detail: number): number {
  if (detail <= 1) return 7; // shouldn't do detail 1
  if (detail === 2) return 9;
  if (detail === 3) return 6;
  if (detail === 4) return 4;
  return 1; // shouldn't do detail 5
}

const detailToSettings = (detail: number) => {
  const pixelSize = detailToPixelSize(detail);
  const paletteSize = detail * 12;
  const threshold = detailToThreshold(detail);
  return { pixelSize, paletteSize, threshold };
};

export const processImage = async (
  app: Application,
  srcTex: Texture,
  detail: number
): Promise<ProcessedImage | undefined> => {
  const { pixelSize, paletteSize, threshold } = detailToSettings(detail);

  try {
    await ensureReady(srcTex);

    // --- 1) Downscale to max size ---
    const bitmapWidth = srcTex.width;
    const bitmapHeight = srcTex.height;
    const maxResW = 800;
    const maxResH = 800;
    const w = Math.min(maxResW, bitmapWidth);
    const h = Math.min(maxResH, bitmapHeight);

    const fitScale = Math.min(w / srcTex.width, h / srcTex.height, 1);
    const dsWScreen = Math.max(1, Math.floor(srcTex.width * fitScale));
    const dsHScreen = Math.max(1, Math.floor(srcTex.height * fitScale));

    // --- 2) Constrain grid to pixelSize ---
    const maxBlocksW = Math.max(1, Math.floor(w / pixelSize));
    const maxBlocksH = Math.max(1, Math.floor(h / pixelSize));

    // How much we must further downsample (uniformly) so grid fits the number of blocks
    const needScaleW = dsWScreen / maxBlocksW;
    const needScaleH = dsHScreen / maxBlocksH;
    const dsScale = Math.max(1, Math.ceil(Math.max(needScaleW, needScaleH)));

    const dsW = Math.max(1, Math.floor(dsWScreen / dsScale));
    const dsH = Math.max(1, Math.floor(dsHScreen / dsScale));

    const downRT = RenderTexture.create({
      width: dsW,
      height: dsH,
      resolution: 1,
    });
    const downSpr = new Sprite(srcTex);
    downSpr.texture.source.scaleMode = "nearest";
    downSpr.width = dsW;
    downSpr.height = dsH;
    app.renderer.render(downSpr, { renderTexture: downRT });
    downSpr.destroy();

    // treat downscaled RT as new source
    const dsTex = new Texture({ source: downRT.source });

    // --- 2) Quantize the downscaled image (no pixelate needed) ---
    const result = await computePalette(app, dsTex, paletteSize);
    if (!result) return undefined;
    const { colorMap: cmap, frequencies } = result;

    const paletteColours = dedupePalette(
      cmap.palette().map(toRgbHexValue),
      frequencies,
      threshold
    );

    const qSpr = new Sprite(dsTex);
    qSpr.texture.source.scaleMode = "nearest";

    const qRT = RenderTexture.create({
      width: dsW,
      height: dsH,
      resolution: 1,
    });
    qRT.source.scaleMode = "nearest";
    app.renderer.render(qSpr, { renderTexture: qRT });

    const extracted = app.renderer.extract.pixels(qRT);
    const pixels = extracted.pixels ?? extracted;
    const width: number = (extracted as { width?: number }).width ?? dsW;
    const height: number = (extracted as { height?: number }).height ?? dsH;

    // --- 3) OFFLOAD heavy per-pixel nearest-colour mapping to a worker ---
    interface PaletteMapRequest {
      kind: "palette-map-request";
      width: number;
      height: number;
      pixels: Uint8ClampedArray<ArrayBufferLike>;
      paletteColours: number[]; // 0xRRGGBB[]
    }

    interface PaletteMapResponse {
      kind: "palette-map-response";
      chosenColors: Uint32Array; // length = width*height, each entry is 0xRRGGBB
    }

    const worker = new Worker(new URL("./image.worker.ts", import.meta.url), {
      type: "module",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);

    const chosenColors: Uint32Array = await new Promise((resolve, reject) => {
      const handleMessage = (ev: MessageEvent<PaletteMapResponse>) => {
        if (ev.data && ev.data.kind === "palette-map-response") {
          worker.removeEventListener("message", handleMessage);
          worker.removeEventListener("error", handleError);
          resolve(ev.data.chosenColors);
          worker.terminate();
        }
      };
      const handleError = (err: ErrorEvent) => {
        worker.removeEventListener("message", handleMessage);
        worker.removeEventListener("error", handleError);
        worker.terminate();
        reject(err.error ?? new Error(err.message));
      };
      worker.addEventListener("message", handleMessage);
      worker.addEventListener("error", handleError);

      const req: PaletteMapRequest = {
        kind: "palette-map-request",
        width,
        height,
        pixels,
        paletteColours,
      };

      // Transfer the underlying buffer for zero-copy
      worker.postMessage(req, [pixels.buffer]);
    });

    // --- 4) Back on main thread: recompute used palette (and sort) + build index map
    const usedPalSet = new Set<number>(chosenColors as unknown as number[]);
    const usedPalArr = sortColors(Array.from(usedPalSet));

    const pixelPalette = new Uint32Array(width * height);
    // map each chosen 0xRRGGBB back to its index in usedPalArr
    for (let i = 0; i < chosenColors.length; i++) {
      const rgb = chosenColors[i] >>> 0;
      const idx = usedPalArr.findIndex((c) => c === rgb);
      if (idx >= 0) pixelPalette[i] = idx >>> 0;
    }

    // cleanup
    qSpr.destroy(true);
    downRT.destroy(true);

    // turn the pixel palette back into a texture for display (nearest-neighbour)
    const srcData = new Uint8ClampedArray(width * height * 4);
    for (let i = 0; i < pixelPalette.length; i++) {
      const palIndex = pixelPalette[i];
      const rgb = usedPalArr[palIndex];
      const r = (rgb >> 16) & 0xff;
      const g = (rgb >> 8) & 0xff;
      const b = rgb & 0xff;
      const di = i * 4;
      srcData[di] = r;
      srcData[di + 1] = g;
      srcData[di + 2] = b;
      srcData[di + 3] = 255;
    }
    const srcImgData = new ImageData(srcData, width, height);
    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = width;
    srcCanvas.height = height;
    const srcCtx = srcCanvas.getContext("2d");
    if (!srcCtx) throw new Error("Failed to get 2D context");
    srcCtx.putImageData(srcImgData, 0, 0);
    const resultText = Texture.from(srcCanvas);

    console.log({
      detail,
      pixelSize,
      paletteSize,
      threshold,
      actualPalette: usedPalArr.length,
      width,
      height,
    });

    return {
      width,
      height,
      palette: usedPalArr,
      pixels: pixelPalette,
      src: srcTex,
      result: resultText,
    };
  } catch (err) {
    console.error("Failed to load image:", err);
  }
};
